import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  WMSTileLayer,
  useMapEvents,
  useMap,
  GeoJSON,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MapParcelPicker.css";

/* ─── Leaflet icon fix (webpack) ─── */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

/* ─── ULDK API ─── */

const ULDK_BASE = "https://uldk.gugik.gov.pl/";

async function getParcelByXY(lng, lat) {
  const url =
    `${ULDK_BASE}?request=GetParcelByXY` +
    `&xy=${lng},${lat},4326` +
    `&result=geom_wkt,teryt,parcel,region,commune,county,voivodeship`;
  const res = await fetch(url);
  const text = await res.text();
  const lines = text.trim().split("\n");
  if (lines[0].trim() !== "0")
    throw new Error("Nie znaleziono działki w tym miejscu");
  if (!lines[1]) throw new Error("Pusta odpowiedź z serwera ULDK");
  const parts = lines[1].split("|");
  return {
    geomWkt: parts[0] || "",
    teryt: parts[1] || "",
    parcelNo: parts[2] || "",
    region: parts[3] || "",
    commune: parts[4] || "",
    county: parts[5] || "",
    voivodeship: parts[6] || "",
  };
}

/* ─── Nominatim reverse geocode ─── */

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    );
    const data = await res.json();
    const addr = data?.address || {};
    const street =
      addr.road ||
      addr.pedestrian ||
      addr.footway ||
      addr.city ||
      addr.town ||
      addr.village ||
      addr.hamlet ||
      "";
    return { street, postalCode: addr.postcode || "" };
  } catch {
    return { street: "", postalCode: "" };
  }
}

/* ─── WKT → GeoJSON ─── */

function wktToGeoJSON(wkt) {
  if (!wkt) return null;
  const parseRing = (s) =>
    s
      .trim()
      .split(",")
      .map((p) => {
        const [x, y] = p.trim().split(/\s+/).map(Number);
        return [x, y];
      });
  try {
    if (wkt.startsWith("MULTIPOLYGON")) {
      const inner = wkt
        .replace(/^MULTIPOLYGON\s*\(\(\(/, "")
        .replace(/\)\)\)\s*$/, "");
      return {
        type: "MultiPolygon",
        coordinates: inner
          .split(")),((")
          .map((p) => p.split("),(").map(parseRing)),
      };
    }
    if (wkt.startsWith("POLYGON")) {
      const inner = wkt.replace(/^POLYGON\s*\(\(/, "").replace(/\)\)\s*$/, "");
      return {
        type: "Polygon",
        coordinates: inner.split("),(").map(parseRing),
      };
    }
  } catch (e) {
    console.warn("[WKT] Parse error:", e);
  }
  return null;
}

function getCentroid(geojson) {
  if (!geojson?.geometry) return null;
  const coords =
    geojson.geometry.type === "Polygon"
      ? geojson.geometry.coordinates[0]
      : geojson.geometry.type === "MultiPolygon"
      ? geojson.geometry.coordinates[0][0]
      : null;
  if (!coords?.length) return null;
  const sum = coords.reduce((a, [x, y]) => [a[0] + x, a[1] + y], [0, 0]);
  return { lng: sum[0] / coords.length, lat: sum[1] / coords.length };
}

/* ─── Sub-components ─── */

function InvalidateSizeOnMount() {
  const map = useMap();
  useEffect(() => {
    const t = [100, 300, 600, 1000].map((ms) =>
      setTimeout(() => map.invalidateSize(), ms)
    );
    return () => t.forEach(clearTimeout);
  }, [map]);
  return null;
}

function UserLocationOnMount() {
  const map = useMap();

  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        map.flyTo([pos.coords.latitude, pos.coords.longitude], 18, {
          duration: 1.4,
        });
      },
      (err) => {
        console.warn("[GPS] Odmowa lub błąd:", err.message);
      },
      { timeout: 8000, maximumAge: 60_000 }
    );
  }, [map]);

  return null;
}

function SearchControl() {
  const map = useMap();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      L.DomEvent.disableClickPropagation(el);
      L.DomEvent.disableScrollPropagation(el);
    }
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&countrycodes=pl&limit=5&q=${encodeURIComponent(
          q
        )}`
      );
      setSuggestions(await r.json());
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 350);
  };

  const flyTo = (item) => {
    map.flyTo([parseFloat(item.lat), parseFloat(item.lon)], 18, {
      duration: 1.2,
    });
    setQuery(item.display_name.split(",").slice(0, 3).join(","));
    setSuggestions([]);
  };

  return (
    <div className="map-search-control" ref={containerRef}>
      <input
        type="text"
        placeholder="Szukaj adresu / miejscowości…"
        value={query}
        onChange={handleInput}
        className="map-search-input"
      />
      {suggestions.length > 0 && (
        <ul className="map-search-results">
          {suggestions.map((s) => (
            <li key={s.place_id} onClick={() => flyTo(s)}>
              {s.display_name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClickHandler({ onLoading, onResult, onError, enabled }) {
  const mouseDownPos = useRef(null);
  useMapEvents({
    mousedown: (e) => {
      mouseDownPos.current = {
        x: e.originalEvent.clientX,
        y: e.originalEvent.clientY,
      };
    },
    click: async (e) => {
      if (!enabled) return;
      if (mouseDownPos.current) {
        const dx = Math.abs(e.originalEvent.clientX - mouseDownPos.current.x);
        const dy = Math.abs(e.originalEvent.clientY - mouseDownPos.current.y);
        if (dx > 5 || dy > 5) return;
      }
      const { lat, lng } = e.latlng;
      onLoading(true);
      onError(null);
      try {
        const parcel = await getParcelByXY(lng, lat);
        const geom = wktToGeoJSON(parcel.geomWkt);
        const feature = geom
          ? { type: "Feature", geometry: geom, properties: {} }
          : null;
        const centroid = getCentroid(feature) || { lat, lng };
        const address = await reverseGeocode(centroid.lat, centroid.lng);
        onResult({
          ...parcel,
          street: address.street,
          postalCode: address.postalCode,
          _geojson: feature,
        });
      } catch (err) {
        onError(err.message || "Błąd identyfikacji działki");
        onResult(null);
      } finally {
        onLoading(false);
      }
    },
  });
  return null;
}

function FitBounds({ geojson }) {
  const map = useMap();
  useEffect(() => {
    if (!geojson) return;
    try {
      const l = L.geoJSON(geojson);
      const b = l.getBounds();
      if (b.isValid()) map.fitBounds(b, { padding: [60, 60], maxZoom: 19 });
    } catch {}
  }, [geojson, map]);
  return null;
}

/* ─── GŁÓWNY KOMPONENT ─── */

export default function MapParcelPicker({ onParcelSelected, disabled }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [parcelData, setParcelData] = useState(null);
  const [parcelGeoJSON, setParcelGeoJSON] = useState(null);

  /* Po każdym udanym kliknięciu od razu przekazujemy dane do rodzica */
  const handleResult = (data) => {
    if (!data) {
      setParcelData(null);
      setParcelGeoJSON(null);
      return;
    }
    setParcelData(data);
    setParcelGeoJSON(data._geojson || null);
    onParcelSelected({
      parcelNo: data.parcelNo,
      municipality: data.commune,
      obreb: data.region,
      teryt: data.teryt,
      street: data.street || "",
      postalCode: data.postalCode || "",
    });
  };

  const handleClear = () => {
    setParcelData(null);
    setParcelGeoJSON(null);
    setError(null);
    onParcelSelected({
      parcelNo: "",
      municipality: "",
      obreb: "",
      postalCode: "",
    });
  };

  return (
    <div className="map-inline-wrapper">
      {/* Pasek nagłówkowy */}
      <div className="map-inline-header">
        <span className="map-inline-title">
          {loading
            ? "Identyfikuję działkę…"
            : parcelData
            ? "Działka wybrana — kliknij inną, aby zmienić"
            : "Kliknij w działkę na mapie"}
        </span>
        {parcelData && !disabled && (
          <button type="button" className="map-clear-btn" onClick={handleClear}>
            Wyczyść
          </button>
        )}
      </div>

      {/* Mapa */}
      <div className="map-inline-container">
        <MapContainer
          center={[51.9, 19.1]}
          zoom={6}
          style={{ width: "100%", height: "100%" }}
          zoomControl={true}
        >
          <InvalidateSizeOnMount />
          <UserLocationOnMount />

          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap"
            maxZoom={22}
          />

          <WMSTileLayer
            url="https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow"
            layers="dzialki,numery_dzialek"
            format="image/png"
            transparent={true}
            maxZoom={22}
            opacity={0.75}
          />

          <SearchControl />

          <ClickHandler
            onLoading={setLoading}
            onResult={handleResult}
            onError={setError}
            enabled={!loading && !disabled}
          />

          {parcelGeoJSON && (
            <>
              <GeoJSON
                key={parcelData?.teryt}
                data={parcelGeoJSON}
                style={{
                  color: "#7dd4d4",
                  weight: 3,
                  fillColor: "#7dd4d4",
                  fillOpacity: 0.18,
                }}
              />
              <FitBounds geojson={parcelGeoJSON} />
            </>
          )}
        </MapContainer>

        {loading && (
          <div className="map-loading-indicator">
            <span className="map-spinner" />
            Identyfikuję działkę…
          </div>
        )}
      </div>

      {/* Błąd */}
      {error && <div className="map-inline-error">{error}</div>}

      {/* Dane działki — wyświetlane pod mapą po wyborze */}
      {parcelData && (
        <div className="map-parcel-result">
          <div className="map-parcel-grid">
            <div className="map-parcel-item">
              <span className="map-parcel-label">Numer działki</span>
              <span className="map-parcel-value">
                {parcelData.parcelNo || "—"}
              </span>
            </div>
            <div className="map-parcel-item">
              <span className="map-parcel-label">Miejscowość / gmina</span>
              <span className="map-parcel-value">
                {parcelData.commune || "—"}
              </span>
            </div>
            <div className="map-parcel-item">
              <span className="map-parcel-label">Obręb</span>
              <span className="map-parcel-value">
                {parcelData.region || "—"}
              </span>
            </div>
            {parcelData.postalCode && (
              <div className="map-parcel-item">
                <span className="map-parcel-label">Kod pocztowy</span>
                <span className="map-parcel-value">
                  {parcelData.postalCode}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
